import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ENVS, USER_API } from "config";
import { useAxios } from "hooks/useAxios";
import { useAnalytics } from "hooks/useAnalytics";
import { useSelector } from "react-redux";
import { AppState } from "store/reducers/Reducer";
import { GetProjectPaymentPageUrl } from "pages/dashboard/payment";
import { persistOtpCheckoutContext } from "pages/dashboard/payment/otpCheckoutContext";
import { PaymentStatus } from "store/types/enum";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";
import { handleAxiosRequest } from "utils";
import { handleToast } from "utils/toasts";
import { catalogKeys, sessionKeys } from "queries/profile.keys";
import { projectKeys } from "queries/project.keys";
import { ANALYTICS_EVENTS } from "analytics";
import { measureOpenAiAdsOrderCreated } from "lib/analytics/openai-ads-pixel";
import { toOtpCheckoutBaseline } from "utils/otpPaymentVerification";
import type { SessionUser } from "types/session-user";
import { parseWriterToStudioCheckout } from "./writerToStudioCheckoutResponse";

type UpgradeLicensePayload = {
  project_id: string;
  target_tier: "studio";
  checkout_ui?: "hosted" | "embedded";
  success_url?: string;
  cancel_url?: string;
  return_url?: string;
};

const useEmbeddedCheckoutPreferred = (): boolean =>
  ENVS.otpEmbeddedCheckoutEnabled && Boolean(ENVS.stripePublishableKey?.trim());

export function useWriterToStudioUpgrade(projectId: string | undefined) {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();
  const { workspaceId } = useWorkspaceIdentifiers();
  const userId = useSelector((state: AppState) => state.auth.user_id);
  const [isLoading, setIsLoading] = useState(false);
  const [embeddedSession, setEmbeddedSession] = useState<{
    clientSecret: string;
    publishableKey: string;
  } | null>(null);

  const dismissEmbeddedCheckout = useCallback(() => {
    setEmbeddedSession(null);
  }, []);

  const onEmbeddedComplete = useCallback(async () => {
    dismissEmbeddedCheckout();
    await queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    await queryClient.invalidateQueries({ queryKey: catalogKeys.all });
    if (projectId) {
      await queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    }
    handleToast("Upgrade complete. Studio features will be available in a moment.", "success");
    trackEvent(ANALYTICS_EVENTS.WRITER_TO_STUDIO_CONVERSION_COMPLETED, {
      userId,
      project_id: projectId,
    });
    measureOpenAiAdsOrderCreated();
  }, [dismissEmbeddedCheckout, projectId, queryClient, trackEvent, userId]);

  const startUpgrade = useCallback(async () => {
    const pid = projectId?.trim();
    if (!pid) {
      handleToast("Project is required to upgrade.", "error");
      return;
    }
    try {
      setIsLoading(true);
      trackEvent(ANALYTICS_EVENTS.WRITER_TO_STUDIO_CONVERSION_STARTED, {
        userId,
        project_id: pid,
      });

      const useEmbedded = useEmbeddedCheckoutPreferred();
      const payload: UpgradeLicensePayload = {
        project_id: pid,
        target_tier: "studio",
      };
      const session = queryClient.getQueryData<SessionUser>(sessionKeys.me());
      const checkoutContext = {
        workspaceId: workspaceId ?? undefined,
        projectId: pid,
        baseline: toOtpCheckoutBaseline(session?.one_time_purchases),
      };
      persistOtpCheckoutContext(checkoutContext);

      if (useEmbedded) {
        payload.checkout_ui = "embedded";
        payload.return_url = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      } else {
        payload.checkout_ui = "hosted";
        payload.success_url = GetProjectPaymentPageUrl(
          PaymentStatus.CREATE_PROJECT_PURCHASE,
          true,
          checkoutContext
        );
        payload.cancel_url = window.location.href;
      }

      const rawResponse = await handleAxiosRequest<unknown>(
        axios.post(USER_API.upgradeWriterToStudioLicense, payload)
      );
      const checkout = parseWriterToStudioCheckout(rawResponse);

      if (checkout.checkoutUi === "embedded") {
        setEmbeddedSession({
          clientSecret: checkout.clientSecret,
          publishableKey: checkout.publishableKey,
        });
        return;
      }

      window.location.assign(checkout.checkoutUrl);
    } catch (error) {
      trackEvent(ANALYTICS_EVENTS.LICENSE_PURCHASE_FAILED, {
        userId,
        context: "writer_to_studio_upgrade",
        projectId: pid,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      handleToast("Could not start upgrade checkout. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [axios, projectId, queryClient, trackEvent, userId, workspaceId]);

  return {
    startUpgrade,
    isLoading,
    embeddedSession,
    dismissEmbeddedCheckout,
    onEmbeddedPurchaseComplete: onEmbeddedComplete,
  };
}
